<template>
  <div>
    <TheNav />
    <TheActions />

    <main class="library">
      <div class="container">
        <TheBackLink />

        <header class="library__head">
          <div class="section-heading">
            <div class="heading-line"/>
            <h1 class="section-title">Library</h1>
          </div>
          <p class="section-subtitle">
            The reference behind the generated work: character sheets that keep a face
            consistent across renders, and the system prompts that produce them. All of
            it is fiction.
          </p>
        </header>

        <!-- Reached by its URL rather than through the card, which is where the
             gate lives. The switch has to be honoured here too, or the lock is
             one link away from meaning nothing. -->
        <div v-if="libraryLocked" class="closed">
          <div class="closed__mark">
            <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round">
              <rect x="4" y="10" width="16" height="10" rx="2" />
              <path d="M8 10V7a4 4 0 0 1 8 0v3" />
            </svg>
          </div>
          <h2 class="closed__title">Not available right now</h2>
          <p class="closed__body">
            The character sheets and system prompts are down at the moment.
            Please check back later.
          </p>
          <NuxtLink to="/" class="closed__home">Back to the site</NuxtLink>
        </div>

        <!-- Two collections, one page. A switcher rather than two long stacked
             sections: the backstories and the prompt bodies are both long-form,
             and putting them on the same scroll buries whichever comes second. -->
        <div v-if="!libraryLocked" ref="switchEl" class="switch" role="tablist" aria-label="Library sections">
          <!-- The line under the active tab, as one element that moves rather
               than a border switched off one tab and on at the next. -->
          <span
            class="switch__glider"
            :class="{ 'is-ready': gliderReady }"
            aria-hidden="true"
            :style="{ '--x': `${glider.x}px`, '--w': `${glider.w}px` }"
          />
          <button
            v-for="tab in tabs"
            :key="tab.id"
            type="button"
            role="tab"
            class="switch__tab"
            :class="{ 'is-active': view === tab.id }"
            :aria-selected="view === tab.id"
            @click="view = tab.id"
          >
            {{ tab.label }}
            <span class="switch__count">{{ tab.count }}</span>
          </button>
        </div>

        <!-- Characters ---------------------------------------------------- -->
        <section v-if="!libraryLocked && view === 'characters'" class="sheets">
          <p v-if="!characterSheets.length" class="empty">
            No character sheets yet.
          </p>

          <ul v-else class="sheets__grid">
            <!-- Placeholders where the live sheets will land, which is at the
                 front now. Without them the compiled-in seeds sit alone for a
                 moment and then get pushed down by rows arriving above, which
                 reads as the page rearranging itself rather than as loading. -->
            <template v-if="pending">
              <li v-for="n in 3" :key="`sheet-skeleton-${n}`" aria-hidden="true">
                <div class="sheet is-skeleton">
                  <span class="sheet__media skeleton"/>
                  <span class="sheet__body">
                    <span class="sk-line sk-line--title skeleton"/>
                    <span class="sk-block">
                      <span class="sk-line skeleton"/>
                      <span class="sk-line sk-line--short skeleton"/>
                    </span>
                  </span>
                </div>
              </li>
            </template>

            <li v-for="sheet in characterSheets" :key="sheet.id" class="cell">
              <button
                type="button"
                class="sheet"
                :class="{ 'is-locked': isLockedSheet(sheet) }"
                @click="openSheet(sheet)"
              >
                <span v-if="isLockedSheet(sheet)" class="cardlock" aria-label="Locked">
                  <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                    <rect x="4" y="10.5" width="16" height="10" rx="2" />
                    <path d="M8 10.5V7.2a4 4 0 0 1 8 0v3.3" />
                  </svg>
                </span>
                <span class="sheet__media">
                  <img
                    v-if="sheet.image"
                    :src="thumb(sheet.image)"
                    :alt="sheet.name"
                    loading="lazy"
                    decoding="async"
                  >
                </span>
                <!-- Two rows: the name, then whatever is known. A sheet with
                     nothing written yet keeps the same shape as a finished one
                     rather than collapsing to a name in an empty panel.

                     `name` is `not null default ''` in the database, so a sheet
                     with no name at all is reachable. Then there is no first
                     row to draw and the panel becomes a single one, with the
                     placeholder centred in the whole of it. -->
                <span class="sheet__body" :class="{ 'is-bare': !sheet.name }">
                  <span v-if="sheet.name" class="sheet__name">{{ sheet.name }}</span>
                  <span
                    v-if="sheet.summary"
                    class="sheet__summary"
                    v-html="renderInline(sheet.summary)"
                  />
                  <span v-else class="sheet__summary is-empty">No details yet</span>
                </span>
              </button>

              <!-- Beside the card rather than inside it. The card is a
                   <button>, and a button within a button is neither valid nor
                   clickable: the parser closes the outer one at it. -->
              <span class="cell__meta">
                <CountBadge
                  v-if="rawCount('sheet', sheet.id)"
                  :count="rawCount('sheet', sheet.id)"
                  kind="view"
                />
                <ShareButton :id="sheet.id" kind="sheet" :title="sheet.name" />
              </span>
            </li>
          </ul>
        </section>

        <!-- Prompts ------------------------------------------------------- -->
        <section v-else-if="!libraryLocked" class="prompts">
          <div v-if="filters.length" class="filters">
            <button
              type="button"
              class="filters__chip"
              :class="{ 'is-active': category === 'all' }"
              @click="category = 'all'"
            >
              All
            </button>
            <button
              v-for="cat in filters"
              :key="cat.id"
              type="button"
              class="filters__chip"
              :class="{ 'is-active': category === cat.id }"
              @click="category = cat.id"
            >
              {{ cat.label }}
              <span class="filters__count">{{ countIn(cat.id) }}</span>
            </button>
          </div>

          <p v-if="!visiblePrompts.length" class="empty">
            {{ systemPrompts.length ? 'Nothing in this category yet.' : 'No prompts published yet.' }}
          </p>

          <ul v-else class="prompts__grid">
            <template v-if="pending">
              <li v-for="n in 3" :key="`prompt-skeleton-${n}`" aria-hidden="true">
                <div class="prompt is-skeleton">
                  <span class="sk-line sk-line--tag skeleton"/>
                  <span class="sk-line sk-line--title skeleton"/>
                  <span class="sk-line sk-line--short skeleton"/>
                  <span class="sk-block sk-block--peek">
                    <span class="sk-line skeleton"/>
                    <span class="sk-line skeleton"/>
                    <span class="sk-line sk-line--short skeleton"/>
                  </span>
                </div>
              </li>
            </template>

            <li v-for="prompt in visiblePrompts" :key="prompt.id" class="cell">
              <button
                type="button"
                class="prompt"
                :class="{ 'is-locked': isLocked(prompt) }"
                @click="openPrompt(prompt)"
              >
                <!-- What the prompt was fed, before what it was asked. Reading
                     the pictures first is the point: the words underneath are
                     about turning these into something. -->
                <span v-if="assetsFor(prompt).length" class="assets" aria-hidden="true">
                  <template v-for="asset in assetsFor(prompt)" :key="asset.url">
                    <span v-if="asset.op" class="assets__op">{{ asset.op }}</span>
                    <img
                      :src="asset.url"
                      :width="asset.width || null"
                      :height="asset.height || null"
                      class="assets__shot"
                      loading="lazy"
                      decoding="async"
                      alt=""
                    >
                  </template>
                </span>

                <span v-if="isLocked(prompt)" class="cardlock" aria-label="Locked">
                  <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                    <rect x="4" y="10.5" width="16" height="10" rx="2" />
                    <path d="M8 10.5V7.2a4 4 0 0 1 8 0v3.3" />
                  </svg>
                </span>

                <span class="prompt__tag">{{ labelFor(prompt.category) }}</span>
                <span class="prompt__title">{{ prompt.title }}</span>
                <span class="prompt__purpose">{{ prompt.purpose }}</span>

                <!-- A few lines of the real thing, so a card is recognisable at
                     a glance without carrying the whole prompt. -->
                <span class="prompt__peek">{{ prompt.body }}</span>

                <span class="prompt__open">
                  Read prompt
                  <svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" stroke-width="2.25" stroke-linecap="round" stroke-linejoin="round">
                    <line x1="5" y1="12" x2="18" y2="12" />
                    <polyline points="13 7 18 12 13 17" />
                  </svg>
                </span>
              </button>

              <span class="cell__meta">
                <CountBadge
                  v-if="rawCount('prompt', prompt.id)"
                  :count="rawCount('prompt', prompt.id)"
                  kind="view"
                />
                <CountBadge
                  v-if="rawCount('download', prompt.id)"
                  :count="rawCount('download', prompt.id)"
                  kind="download"
                />
                <ShareButton :id="prompt.id" kind="prompt" :title="prompt.title" />
              </span>
            </li>
          </ul>
        </section>
      </div>
    </main>

    <Teleport to="body">
      <Transition name="sheet-fade">
        <div v-if="openedPrompt" class="sheet-overlay" @click="openedPrompt = null">
          <div class="prompt-detail" role="dialog" aria-modal="true" @click.stop>
            <button type="button" class="sheet-detail__close" aria-label="Close" @click="openedPrompt = null">
              <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round">
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>

            <header class="prompt-detail__head">
              <!-- Beside the tag rather than out to the right, where the close
                   button already is. -->
              <div class="detail__top">
                <span class="prompt__tag">{{ labelFor(openedPrompt.category) }}</span>
                <ShareButton
                  :id="openedPrompt.id"
                  kind="prompt"
                  :title="openedPrompt.title"
                />
              </div>
              <h2 class="prompt-detail__title">{{ openedPrompt.title }}</h2>
              <p class="prompt-detail__purpose">{{ openedPrompt.purpose }}</p>
            </header>

            <!-- Everything above is about the prompt. This is the prompt. -->
            <h3 class="prompt-detail__heading">Prompt</h3>

            <pre class="prompt-detail__body" data-lenis-prevent>{{ openedPrompt.body }}</pre>

            <!-- The prompt leaves as a file, and the file costs an address.
                 A courtesy gate rather than a wall: the text is in the page
                 that asked, so anyone unwilling to give one does not have to.
                 It asks at the one moment somebody actively wants something. -->
            <div class="grab">
              <template v-if="!asking">
                <button type="button" class="prompt__get" @click="asking = true">
                  Download .txt
                  <svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" stroke-width="2.25" stroke-linecap="round" stroke-linejoin="round">
                    <path d="M12 4v11" />
                    <polyline points="7 11 12 16 17 11" />
                    <path d="M5 20h14" />
                  </svg>
                </button>
                <p class="grab__note">Enter your email to download.</p>
              </template>

              <form v-else-if="grabState !== 'done'" class="grab__form" @submit.prevent="grab">
                <label class="grab__trap" aria-hidden="true">
                  Leave this field empty
                  <input v-model="grabBot" tabindex="-1" autocomplete="off">
                </label>

                <label class="grab__field">
                  <span>Email</span>
                  <input
                    v-model="grabEmail"
                    type="email"
                    inputmode="email"
                    autocomplete="email"
                    placeholder="you@example.com"
                    maxlength="254"
                    required
                    :disabled="grabState === 'sending'"
                  >
                </label>

                <div class="grab__row">
                  <button type="submit" class="prompt__get" :disabled="grabState === 'sending'">
                    {{ grabState === 'sending' ? 'One moment…' : 'Download' }}
                  </button>
                  <button type="button" class="grab__cancel" @click="asking = false">Cancel</button>
                </div>

                <p v-if="grabError" class="grab__error" role="status">{{ grabError }}</p>
              </form>

              <p v-else class="grab__done" role="status">
                Saved to your downloads. Thanks.
              </p>
            </div>
          </div>
        </div>
      </Transition>
    </Teleport>

    <Teleport to="body">
      <Transition name="sheet-fade">
        <div v-if="lockedPrompt" class="sheet-overlay" @click="lockedPrompt = null">
          <div class="locknote" role="dialog" aria-modal="true" @click.stop>
            <span class="locknote__mark" aria-hidden="true">
              <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">
                <rect x="4" y="10.5" width="16" height="10" rx="2" />
                <path d="M8 10.5V7.2a4 4 0 0 1 8 0v3.3" />
              </svg>
            </span>

            <h3 class="locknote__title">This item is locked</h3>

            <button type="button" class="locknote__close" @click="lockedPrompt = null">
              Close
            </button>
          </div>
        </div>
      </Transition>
    </Teleport>

    <!-- Teleported for consistency with the rest of the site's overlays, and
         so it is never affected by a transformed ancestor here later. -->
    <Teleport to="body">
      <Transition name="sheet-fade">
        <div v-if="openedSheet" class="sheet-overlay" @click="openedSheet = null">
          <div class="sheet-detail" role="dialog" aria-modal="true" @click.stop>
            <button type="button" class="sheet-detail__close" aria-label="Close" @click="openedSheet = null">
              <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round">
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>

            <!-- The picture follows the reading. Each beat of the story names
                 the image that should be on screen while it is read, so
                 reaching the part about a kingdom puts the kingdom in front of
                 you. Stacked and cross-faded rather than swapping one src,
                 which would flash on every change. -->
            <div class="sheet-detail__media">
              <div
                v-for="(visual, index) in visuals"
                :key="`${visual}-${index}`"
                class="sheet-detail__frame"
                :class="{ 'is-on': index === activeChapter }"
              >
                <!-- Blurred copy fills the column; the real picture sits whole
                     on top. Cropping to fit only ever suited tall images, and
                     even those lost their edges. -->
                <img :src="visual" alt="" class="sheet-detail__wash" decoding="async">
                <img
                  :src="visual"
                  :alt="index === activeChapter ? openedSheet.name : ''"
                  class="sheet-detail__pic"
                  decoding="async"
                >
              </div>

              <!-- Reversed control: these do not just report where you are,
                   they take you there — the story scrolls to the passage each
                   picture belongs to. Arrows step through in order; the dots
                   jump straight to one. -->
              <div v-if="visuals.length > 1" class="sheet-detail__controls">
                <button
                  type="button"
                  class="sheet-detail__arrow"
                  aria-label="Previous part"
                  :disabled="activeChapter === 0"
                  @click="stepChapter(-1)"
                >
                  <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round">
                    <polyline points="15 18 9 12 15 6" />
                  </svg>
                </button>

                <div class="sheet-detail__dots">
                  <button
                    v-for="(chapter, index) in chapters"
                    :key="`dot-${chapter.id}`"
                    type="button"
                    class="sheet-detail__dot"
                    :class="{ 'is-active': index === activeChapter }"
                    :aria-label="chapter.label || `Part ${index + 1}`"
                    :title="chapter.label || `Part ${index + 1}`"
                    @click="goToChapter(index)"
                  />
                </div>

                <button
                  type="button"
                  class="sheet-detail__arrow"
                  aria-label="Next part"
                  :disabled="activeChapter === chapters.length - 1"
                  @click="stepChapter(1)"
                >
                  <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round">
                    <polyline points="9 18 15 12 9 6" />
                  </svg>
                </button>
              </div>

              <span v-if="currentLabel" class="sheet-detail__caption">{{ currentLabel }}</span>
            </div>

            <div ref="storyEl" class="sheet-detail__body" data-lenis-prevent>
              <!-- Stays put while the story runs under it. Whose story this is
                   is the one thing worth having on screen the whole way down,
                   and it was the first thing to leave. -->
              <div class="sheet-detail__title">
                <div class="detail__top">
                  <span class="sheet-detail__eyebrow">Character sheet</span>
                  <ShareButton
                    :id="openedSheet.id"
                    kind="sheet"
                    :title="openedSheet.name"
                  />
                </div>
                <h2 class="sheet-detail__name">{{ openedSheet.name }}</h2>
              </div>

              <ul v-if="openedSheet.traits?.length" class="sheet-detail__traits">
                <li v-for="trait in openedSheet.traits" :key="trait">{{ trait }}</li>
              </ul>

              <!-- The opening, before any of the parts.
                   It was written in the Details tab and shown only on the card,
                   clamped to three lines, then dropped entirely the moment the
                   story was opened. So the one passage that sets a character up
                   was the one passage nobody could read in full. Inline
                   rendering, because that is the subset the field offers. -->
              <p
                v-if="openedSheet.summary"
                class="sheet-detail__lede"
                v-html="renderInline(openedSheet.summary)"
              />

              <template v-if="chapters.length">
                <section
                  v-for="(chapter, index) in chapters"
                  :key="chapter.id"
                  ref="chapterEls"
                  class="chapter"
                  :data-index="index"
                >
                  <h3 v-if="chapter.label" class="chapter__label">{{ chapter.label }}</h3>
                  <!-- Written as Markdown in the admin and rendered by the same
                       function its preview uses, so what was composed is what
                       appears. Safe for v-html by construction: the renderer
                       escapes every character of input before any rule runs,
                       so nothing typed can become a tag. -->
                  <div class="chapter__text prose" v-html="renderMarkdown(chapter.text)"/>
                </section>
              </template>
              <p v-else class="sheet-detail__story is-empty">Backstory not written yet.</p>
            </div>
          </div>
        </div>
      </Transition>
    </Teleport>
  </div>
</template>

<script setup>
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import {
  characterSheets as builtInSheets,
  groupOf,
  personalInfo,
  promptCategories,
  systemPrompts as builtInPrompts,
} from '~/data/content';

usePageScroll();

// Seeded with what was compiled in, then replaced by whatever has been written
// from /admin. Seeding rather than starting empty means the page is never blank
// while the request is in flight, and stays populated if it fails.
const characterSheets = ref(builtInSheets);
const systemPrompts = ref(builtInPrompts);

const { load, useSettings, usePending } = useLiveContent();

// True until the one live-content request settles, so the grids can hold a
// place for the rows that are on their way rather than filling in silently.
const pending = usePending();

// Closed from the admin. The server withholds the rows as well, so this is the
// page being honest about it rather than the page being the lock.
const liveSettings = useSettings();
const libraryLocked = computed(() => liveSettings.value.libraryLocked === true);

const route = useRoute();

onMounted(async () => {
  const [sheets, prompts] = await Promise.all([
    load('sheets', builtInSheets),
    load('prompts', builtInPrompts),
  ]);
  characterSheets.value = sheets;
  systemPrompts.value = prompts;

  // Arrived from a shared link, or from the gallery, which sends the character
  // rather than the page. Opening it here rather than making the reader find it
  // again is the whole point of the link having carried an id.
  //
  // The lock comes first. A closed library shows nothing, and a link made while
  // it was open must not be a way around that.
  if (libraryLocked.value) return;

  const wantedSheet = route.query.sheet;
  const wantedPrompt = route.query.prompt;

  if (wantedSheet) {
    const sheet = sheets.find((row) => row.id === wantedSheet);
    if (!sheet) return;

    view.value = 'characters';
    await nextTick();
    openSheet(sheet);
  } else if (wantedPrompt) {
    const prompt = prompts.find((row) => row.id === wantedPrompt);
    if (!prompt) return;

    // The prompts are the other tab, so the page has to be switched to it
    // before there is anything to open.
    view.value = groupOf(prompt.category) === 'system' ? 'system' : 'prompts';
    await nextTick();
    openPrompt(prompt);
  } else {
    return;
  }

  // A bookmarked link should not keep reopening the same thing for ever.
  window.history.replaceState(window.history.state, '', window.location.pathname);
});

useHead({
  title: `Library - ${personalInfo.fullName}`,
  meta: [
    {
      name: 'description',
      content: 'Character sheets and system prompts behind the generated work.',
    },
    { property: 'og:title', content: `Library - ${personalInfo.fullName}` },
    { property: 'og:description', content: 'Character sheets and system prompts behind the generated work.' },
    { name: 'twitter:title', content: `Library - ${personalInfo.fullName}` },
    { name: 'twitter:description', content: 'Character sheets and system prompts behind the generated work.' },
  ],
});

// The small copy of a built-in render, for the card grid. A card draws its
// portrait at about 215px wide while the files in /images/ai are the full
// renders at 1024x1536 and up, which is 25 times more pixels than are shown and
// 6MB of bitmap decoded per card.
//
// Uploaded portraits come from Netlify Blobs rather than /images/ai, so this
// leaves them alone, which is right: they are already capped at 1600 on the
// long edge on the way up. The reader below keeps the full picture, because
// there it is actually displayed large.
const thumb = (src) => (src || '').replace('/images/ai/', '/images/ai/thumb/');

const view = ref('characters');
const category = ref('all');
const openedSheet = ref(null);
const openedPrompt = ref(null);

// The placeholders compiled into the site, there so the page has something to
// draw before anything real is published. They are examples of the shape, not
// prompts anybody should be reading, so they are shown and not opened.
//
// Known by their id rather than by a flag, because that is what they already
// carry, and because live content replaces the whole list: publish a real
// prompt and there is no stub left to lock.
const isLocked = (prompt) => String(prompt?.id ?? '').startsWith('stub-');

// The sheets compiled in are placeholders for the same reason, and are known
// the same way: by being one of them. Their ids are slugs and a published one
// gets a uuid, and in any case live content replaces the whole list, so
// publishing a real sheet leaves none of these to lock.
const builtInSheetIds = new Set(builtInSheets.map((sheet) => sheet.id));

const isLockedSheet = (sheet) => builtInSheetIds.has(String(sheet?.id ?? ''));

const lockedPrompt = ref(null);

function openPrompt(prompt) {
  if (isLocked(prompt)) {
    lockedPrompt.value = prompt;
    return;
  }
  openedPrompt.value = prompt;
}

// Opening one and reading it is the look. Clicking through a few to find the
// one you wanted is not, which is what the dwell is there to tell apart.
useDwellView('sheet', () => openedSheet.value?.id);
useDwellView('prompt', () => openedPrompt.value?.id);

const { useCounts: useItemCounts } = useLiveContent();
const itemCounts = useItemCounts();

function rawCount(kind, id) {
  return Number(itemCounts.value?.[kind]?.[id]) || 0;
}




// A prompt that sets a model up and a prompt that asks for a picture are read
// for different reasons, so they get a tab each rather than a shared list with
// a filter doing the work of a heading.
const systemOnly = computed(() =>
  systemPrompts.value.filter((p) => groupOf(p.category) === 'system')
);

const generativeOnly = computed(() =>
  systemPrompts.value.filter((p) => groupOf(p.category) === 'generative')
);

const tabs = computed(() => [
  { id: 'characters', label: 'Character sheets', count: characterSheets.value.length },
  { id: 'system', label: 'System prompts', count: systemOnly.value.length },
  { id: 'prompts', label: 'Prompts', count: generativeOnly.value.length },
]);

// Whichever list the open tab is showing, before the category filter narrows it.
const inView = computed(() => (view.value === 'system' ? systemOnly.value : generativeOnly.value));

// The chips, which are the categories of the open tab and nobody else's. Drawn
// only when there is a choice to make: one category is not a filter.
const filters = computed(() => {
  const group = view.value === 'system' ? 'system' : 'generative';
  const present = promptCategories.filter(
    (cat) => cat.group === group && inView.value.some((p) => p.category === cat.id)
  );
  return present.length > 1 ? present : [];
});

// The moving underline.
//
// Measured rather than computed. Explore can say "half the row, shifted by one"
// because its two pills are forced to equal widths; these are as wide as their
// own words and their counts, so where the line goes is a question only the
// laid-out DOM can answer.
const switchEl = ref(null);
const glider = ref({ x: 0, w: 0 });
// Held still until it has been measured once, or the first paint slides it in
// from the left edge instead of simply having it in place.
const gliderReady = ref(false);

async function measureGlider() {
  await nextTick();

  const root = switchEl.value;
  if (!root) return;

  const active = root.querySelector('.switch__tab.is-active');
  if (!active) return;

  // Rectangles, not offsetLeft: that is measured against the nearest positioned
  // ancestor, which is only this row by coincidence.
  const a = active.getBoundingClientRect();
  const r = root.getBoundingClientRect();

  glider.value = { x: a.left - r.left, w: a.width };

  if (!gliderReady.value) {
    // A frame after the first placement, not with it, or the transition it
    // turns on has a starting position to travel from and the line slides in
    // from the left edge on every load.
    requestAnimationFrame(() => {
      gliderReady.value = true;
    });
  }
}

// The counts arrive with the live content and change how wide a tab is, so the
// line has to be taken again when they land, not only when a tab is pressed.
watch([view, tabs, libraryLocked], measureGlider, { flush: 'post' });

onMounted(() => {
  measureGlider();
  window.addEventListener('resize', measureGlider);

  // A tab is as wide as its text, and its text is not final until the webfont
  // it is set in has loaded. Measured before that, the line is placed against
  // the fallback and left sitting short of the label.
  document.fonts?.ready.then(measureGlider).catch(() => {});
});

onBeforeUnmount(() => window.removeEventListener('resize', measureGlider));

const visiblePrompts = computed(() =>
  category.value === 'all'
    ? inView.value
    : inView.value.filter((p) => p.category === category.value)
);

const countIn = (id) => inView.value.filter((p) => p.category === id).length;

// A chip chosen on one tab means nothing on the other, and left set it would
// hide everything there.
watch(view, () => {
  category.value = 'all';
});

// What a generative prompt was fed, at most the four the strip is drawn for,
// each carrying the mark that goes before it.
//
// The row is a sum and the last picture is what it came to, so the mark before
// that one is an equals and the rest are pluses. One picture is not a sum: it
// is just what went in, and it gets no marks at all.
const assetsFor = (prompt) => {
  const list = Array.isArray(prompt?.assets) ? prompt.assets.slice(0, 4) : [];

  return list.map((asset, i) => ({
    ...asset,
    op: i === 0 ? '' : i === list.length - 1 ? '=' : '+',
  }));
};
const labelFor = (id) => promptCategories.find((c) => c.id === id)?.label ?? id;

// --- character detail: picture follows the reading -------------------------

const storyEl = ref(null);
const chapterEls = ref([]);
const activeChapter = ref(0);

const chapters = computed(() => openedSheet.value?.chapters ?? []);

// One picture per chapter. A chapter with no image of its own keeps whatever
// was already showing, falling back to the portrait — so only the beats that
// actually change the view need to name an image.
const visuals = computed(() => {
  const sheet = openedSheet.value;
  if (!sheet) return [];

  // A sheet with no story written yet still has a face to show. Deriving the
  // pictures purely from the chapters left the column empty for every character
  // until its backstory existed.
  if (!chapters.value.length) return sheet.image ? [sheet.image] : [];

  let current = sheet.image ?? null;
  return chapters.value
    .map((chapter) => {
      if (chapter.image) current = chapter.image;
      return current;
    })
    .filter(Boolean);
});

const currentLabel = computed(() => chapters.value[activeChapter.value]?.label ?? '');

// Which chapter is being read, worked out from the scroll position rather than
// by observing intersections.
//
// An IntersectionObserver with a narrow band across the middle was tried first
// and is wrong here: it assumes every chapter is taller than the band. A short
// chapter never centres, so opening the reader jumped straight to chapter three
// and the final chapter could never become active at all — there is no scroll
// left below it to bring it to the middle.
//
// A reading line a third of the way down the panel has neither problem: the
// active chapter is simply the last one to have passed it.
const READING_LINE = 0.33;

let scrollRaf = null;

function updateActiveChapter() {
  const root = storyEl.value;
  const sections = (chapterEls.value || []).filter(Boolean);
  if (!root || sections.length < 2) return;

  // Reaching the end always means the last chapter, however short it is.
  if (root.scrollTop + root.clientHeight >= root.scrollHeight - 4) {
    activeChapter.value = sections.length - 1;
    return;
  }

  const line = root.scrollTop + root.clientHeight * READING_LINE;
  let index = 0;
  sections.forEach((section, i) => {
    if (section.offsetTop - root.offsetTop <= line) index = i;
  });
  activeChapter.value = index;
}

function onStoryScroll() {
  if (scrollRaf) return;
  scrollRaf = requestAnimationFrame(() => {
    scrollRaf = null;
    updateActiveChapter();
  });
}

function watchChapters() {
  const root = storyEl.value;
  if (!root) return;

  root.removeEventListener('scroll', onStoryScroll);
  root.addEventListener('scroll', onStoryScroll, { passive: true });
  updateActiveChapter();
}

function unwatchChapters() {
  storyEl.value?.removeEventListener('scroll', onStoryScroll);
  if (scrollRaf) cancelAnimationFrame(scrollRaf);
  scrollRaf = null;
}

// One step either way. Clamped rather than wrapping: the arrows disable at the
// ends, so jumping from the last beat back to the first would contradict what
// the control is showing.
function stepChapter(delta) {
  const next = activeChapter.value + delta;
  if (next < 0 || next > chapters.value.length - 1) return;
  goToChapter(next);
}

// Clicking a control moves the story, not just the picture.
function goToChapter(index) {
  const root = storyEl.value;
  const section = (chapterEls.value || [])[index];
  if (!root || !section) return;

  activeChapter.value = index;
  root.scrollTo({
    // Measured against the panel's own scroll origin rather than the page's.
    top: section.offsetTop - root.offsetTop - 12,
    behavior: 'smooth',
  });
}

function openSheet(sheet) {
  if (isLockedSheet(sheet)) {
    lockedPrompt.value = sheet;
    return;
  }

  openedSheet.value = sheet;
  activeChapter.value = 0;
  // A frame late: the sections have to exist before they can be observed.
  nextTick(watchChapters);
}

watch(openedSheet, (sheet) => {
  if (!sheet) unwatchChapters();
});

// --- taking a prompt away --------------------------------------------------

const asking = ref(false);
const grabEmail = ref('');
const grabBot = ref('');
// idle | sending | done
const grabState = ref('idle');
const grabError = ref('');

// Reset whenever a different prompt is opened, so the confirmation from the
// last one is not sitting on the next.
watch(openedPrompt, () => {
  asking.value = false;
  grabEmail.value = '';
  grabBot.value = '';
  grabState.value = 'idle';
  grabError.value = '';
});

// The file is built here rather than served, because the prompt is already in
// this page. Fetching it back from the server would be a second round trip for
// text that is on screen.
function toPlainText(prompt) {
  const label = promptCategories.find((c) => c.id === prompt.category)?.label ?? prompt.category;
  // Read at download time rather than hardcoded, so the line is right on the
  // deployed site and on localhost without either being wrong.
  const origin = typeof window === 'undefined' ? '' : window.location.origin;

  // Text, not Markdown. A prompt is pasted into a model, not rendered, and the
  // fences and asterisks that kept it safe from a Markdown reader were just
  // more characters to strip back out at the other end.
  return [
    prompt.title,
    `Category: ${label}`,
    '',
    prompt.purpose,
    '',
    '----------------------------------------',
    '',
    prompt.body,
    '',
    '----------------------------------------',
    `From ${personalInfo.fullName}, ${origin}/library/`,
    '',
  ].join('\n');
}

function fileNameFor(prompt) {
  const slug = prompt.title
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
  return `${slug || 'system-prompt'}.txt`;
}

function saveFile(name, text) {
  const url = URL.createObjectURL(new Blob([text], { type: 'text/plain;charset=utf-8' }));
  const link = document.createElement('a');
  link.href = url;
  link.download = name;
  document.body.appendChild(link);
  link.click();
  link.remove();
  // Not revoked synchronously: some browsers have not finished reading the blob
  // by the time the click handler returns, and revoking early cancels the save.
  setTimeout(() => URL.revokeObjectURL(url), 10000);
}

// The address is recorded before the file is handed over, so a failure to
// record is a failure to download rather than a silent hole in the record.
async function grab() {
  const prompt = openedPrompt.value;
  if (!prompt || grabState.value === 'sending') return;

  const email = grabEmail.value.trim();
  if (!email) return;

  grabState.value = 'sending';
  grabError.value = '';

  try {
    const response = await fetch('/.netlify/functions/prompt-download', {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({
        email,
        promptId: prompt.id,
        promptTitle: prompt.title,
        // Which of the two lists it came from, worked out here so the mapping
        // lives in one place rather than being repeated in a function that
        // cannot import it.
        promptGroup: groupOf(prompt.category),
        botField: grabBot.value,
      }),
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(data.error || 'that did not go through');

    saveFile(fileNameFor(prompt), toPlainText(prompt));
    grabState.value = 'done';
  } catch (e) {
    grabState.value = 'idle';
    grabError.value = e.message;
  }
}

function onKey(event) {
  if (event.key !== 'Escape') return;
  openedSheet.value = null;
  openedPrompt.value = null;
  lockedPrompt.value = null;
}

onMounted(() => document.addEventListener('keydown', onKey));
onBeforeUnmount(() => {
  document.removeEventListener('keydown', onKey);
  unwatchChapters();
});
</script>

<style lang="scss" scoped>
.library {
  min-height: 100vh;
  min-height: 100svh;
  padding: clamp(5rem, 12vh, 8rem) 0 clamp(4rem, 10vh, 7rem);
}

.library__head {
  max-width: 640px;
  margin-bottom: clamp(1.75rem, 4vh, 2.75rem);
}

.section-heading {
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: clamp(0.5rem, 1.2vh, 0.75rem);

  .heading-line {
    display: none;
    width: 50px;
    height: 3px;
    flex-shrink: 0;
  }

  .section-title {
    font-size: clamp(1.5rem, 1.6vw + 1.6vh, 2.25rem);
    font-weight: 800;
    color: var(--text);
    margin: 0;
    font-family: 'Orbitron', sans-serif;
    letter-spacing: 4px;
    background: linear-gradient(135deg, var(--accent) 0%, rgba(var(--accent-rgb), 0.8) 50%, var(--text) 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
    text-transform: uppercase;
  }
}

.section-subtitle {
  color: var(--muted);
  font-size: clamp(0.85rem, 0.5vw + 0.8vh, 1rem);
  line-height: 1.6;
  margin: 0;
}

.switch {
  position: relative;
  display: flex;
  gap: 0.4rem;
  margin-bottom: clamp(1.5rem, 3.5vh, 2.25rem);
  border-bottom: 1px solid rgba(var(--border-rgb), 0.4);
}

.switch__glider {
  position: absolute;
  left: 0;
  bottom: -1px;
  height: 2px;
  width: var(--w, 0);
  border-radius: 2px;
  background: var(--accent);
  transform: translateX(var(--x, 0));
  // Still until it has been placed. The class is added a frame after the first
  // measurement, so there is a position to move from before there is a move.
  transition: none;

  &.is-ready {
    transition:
      transform 0.32s cubic-bezier(0.4, 0, 0.2, 1),
      width 0.32s cubic-bezier(0.4, 0, 0.2, 1);
  }
}

.switch__tab {
  display: flex;
  align-items: center;
  gap: 0.45rem;
  padding: 0.6rem 0.9rem;
  border: none;
  background: none;
  font: inherit;
  font-size: 0.85rem;
  font-weight: 600;
  color: var(--muted);
  cursor: pointer;
  border-bottom: 2px solid transparent;
  margin-bottom: -1px;
  transition: color 0.2s ease-in-out, border-color 0.2s ease-in-out;

  &:hover {
    color: var(--text);
  }

  // The line itself is drawn by .switch__glider. The transparent border stays
  // so the row reserves its two pixels either way and the labels do not shift
  // as the active tab changes.
  &.is-active {
    color: var(--text);
  }
}

@media (prefers-reduced-motion: reduce) {
  .switch__glider.is-ready {
    transition: none;
  }
}

.switch__count,
.filters__count {
  font-family: 'Orbitron', sans-serif;
  font-size: 0.62rem;
  color: var(--muted);
}

// Character sheets ----------------------------------------------------------

.sheets__grid {
  list-style: none;
  margin: 0;
  padding: 0;
  display: grid;
  // A card is a picture and a text panel of equal width side by side, so a
  // track has to be about twice what the portrait card alone needed.
  //
  // 330px is chosen to hold three across at the widths that matter rather than
  // for its own sake: the container is min(1400px, 88vw), which gives about
  // 1060px of content at a 1280 viewport and about 1340px once the 1400 cap
  // bites. Three columns at the first needs a minimum under ~342px, and at the
  // second needs one over ~322px to stop a fourth appearing. 330 sits in that
  // window. Below about a 1100 viewport it drops to two, which is right.
  grid-template-columns: repeat(auto-fill, minmax(330px, 1fr));
  gap: clamp(0.75rem, 1.5vw, 1.25rem);
}

.sheet {
  display: flex;
  // Picture left, name and summary right. The portrait keeps its 3:4 and the
  // text column takes whatever is left.
  flex-direction: row;
  align-items: stretch;
  width: 100%;
  padding: 0;
  border: 1px solid rgba(var(--border-rgb), 0.35);
  border-radius: 14px;
  overflow: hidden;
  background:
    linear-gradient(rgba(var(--muted-rgb), 0.04), rgba(var(--muted-rgb), 0.04)),
    var(--background);
  font: inherit;
  text-align: left;
  cursor: pointer;
  // The lift belongs to .cell, which holds the card and its share button both.
  transition: border-color 0.2s ease-in-out;

  &:hover,
  &:focus-visible {
    border-color: rgba(var(--accent-rgb), 0.45);
  }
}

.sheet__media {
  display: block;
  // Half the card exactly, so the picture keeps the size it had as a portrait
  // card and the text panel beside it matches.
  flex: 0 0 50%;
  aspect-ratio: 3 / 4;
  overflow: hidden;
  background: rgba(var(--muted-rgb), 0.08);
  // The portrait keeps its 3:4 whatever the text beside it does. Without this
  // it is a flex item with the default shrink of 1: the grid stretches every
  // card in a row to the tallest, and in a card whose text had grown, the
  // picture was squashed to make room. That was the distortion, and it showed
  // on the cards next to the long one as much as on the long one itself.
  flex-shrink: 0;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
  }
}

.sheet__body {
  // Two rows: the name, then everything else. `1fr` on the second is what stops
  // the panel collapsing around short content, so a sheet with only a name is
  // the same shape as a finished one.
  display: grid;
  grid-template-rows: auto 1fr;
  gap: 0.5rem;

  // No name, so no first row to draw and nothing to divide from. The
  // placeholder gets the whole panel to centre itself in.
  &.is-bare {
    grid-template-rows: 1fr;

    .sheet__summary {
      padding-top: 0;
      border-top: 0;
    }
  }
  // min-width: 0 is what makes the clamps below work at all. A grid item's
  // default minimum is its content, so without this the text column refuses to
  // be narrower than its longest line and pushes the card wide instead of
  // wrapping inside it.
  flex: 1 1 50%;
  min-width: 0;
  padding: 0.8rem 0.9rem;
}

// Both clamped, like every other card on the site: the project cards, the
// writing cards and the prompt peek all do this. The character card was the one
// that did not, so a name or a summary of any real length grew the card, and
// the grid row grew with it.
//
// overflow-wrap because a clamp does not help against a single unbroken run of
// characters, which is what a pasted URL is.
.sheet__name {
  font-size: 0.9rem;
  font-weight: 700;
  color: var(--text);
  overflow-wrap: anywhere;
  overflow: hidden;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
}

.sheet__summary {
  font-size: 0.75rem;
  line-height: 1.5;
  color: var(--muted);
  overflow-wrap: anywhere;
  overflow: hidden;
  display: -webkit-box;
  // The panel is a full portrait tall now, so there is room for more than the
  // three lines that fitted when the picture sat above the text.
  -webkit-line-clamp: 5;
  -webkit-box-orient: vertical;

  // The second row, and it fills whatever the name leaves. Bordered so the two
  // rows read as a structure rather than as text that happened to wrap.
  padding-top: 0.5rem;
  border-top: 1px solid rgba(var(--border-rgb), 0.35);

  // Sits at the top of its row rather than stretching to fill it. This is what
  // makes the clamp above work: -webkit-line-clamp puts the ellipsis after the
  // fifth line but does no clipping of its own, relying on the box being the
  // height of the lines it kept. Stretched by `1fr` the box is taller than
  // that, so every remaining line stayed visible below the ellipsis. The row
  // still holds its height, so the panel does not collapse.
  align-self: start;

  // Nothing written yet. Takes the whole of whatever row it is in and sits in
  // the middle of it, so the card reads as deliberately empty rather than as
  // one that failed to load. Overrides the -webkit-box above, which is only
  // there for clamping and has nothing to clamp here.
  &.is-empty {
    align-self: stretch;
    display: flex;
    align-items: center;
    justify-content: center;
    text-align: center;
    // The 0.5rem the summary carries to sit clear of the divider would push
    // this off centre: align-items centres within the content box, which
    // excludes padding, so a top pad and no bottom pad lands the text low. The
    // divider stays; the space below it is what this centres in.
    padding-top: 0;
    color: rgba(var(--muted-rgb), 0.7);
    font-style: italic;
  }
}

// Skeletons ------------------------------------------------------------------
//
// These reuse .sheet and .prompt so the placeholder occupies exactly the space
// its replacement will. Only the insides differ.

.sheet.is-skeleton,
.prompt.is-skeleton {
  cursor: default;

  &:hover {
    border-color: rgba(var(--border-rgb), 0.35);
  }
}

// The prompt card is a column, and its placeholder is one too. The character
// card is not: it is a portrait beside a panel of text, at every width.
//
// These were folded into one rule while tidying duplicate selectors, which
// handed the character placeholder a column direction and stood its portrait on
// top of its text while the real card underneath was side by side.
.prompt.is-skeleton {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.sk-line {
  display: block;
  height: 0.72rem;
  border-radius: 4px;
}

.sk-line--title {
  height: 0.95rem;
  width: 70%;
}

.sk-line--tag {
  height: 0.6rem;
  width: 3.5rem;
}

.sk-line--short {
  width: 55%;
}

.sk-block {
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
  align-self: start;
  padding-top: 0.5rem;
  border-top: 1px solid rgba(var(--border-rgb), 0.35);
}

.sk-block--peek {
  margin-top: 0.25rem;
  border-top: 0;
  padding-top: 0;
}

// Prompts -------------------------------------------------------------------

.filters {
  display: flex;
  flex-wrap: wrap;
  gap: 0.4rem;
  margin-bottom: clamp(1rem, 2.5vh, 1.75rem);
}

.filters__chip {
  display: flex;
  align-items: center;
  gap: 0.4rem;
  padding: 0.4rem 0.85rem;
  border-radius: 999px;
  border: 1px solid rgba(var(--border-rgb), 0.45);
  background: transparent;
  font: inherit;
  font-size: 0.78rem;
  font-weight: 600;
  color: var(--muted);
  cursor: pointer;
  transition: color 0.2s ease-in-out, border-color 0.2s ease-in-out, background 0.2s ease-in-out;

  &:hover {
    color: var(--text);
  }

  &.is-active {
    color: var(--accent);
    border-color: rgba(var(--accent-rgb), 0.5);
    background: rgba(var(--accent-rgb), 0.07);
  }
}

.prompts__grid {
  list-style: none;
  margin: 0;
  padding: 0;
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: clamp(0.75rem, 1.5vw, 1.25rem);
}

.prompt {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 0.35rem;
  width: 100%;
  height: 100%;
  padding: clamp(1rem, 2vh, 1.35rem);
  border: 1px solid rgba(var(--border-rgb), 0.35);
  border-radius: 14px;
  background:
    linear-gradient(rgba(var(--muted-rgb), 0.03), rgba(var(--muted-rgb), 0.03)),
    var(--background);
  font: inherit;
  text-align: left;
  cursor: pointer;
  // The lift belongs to .cell, which holds the card and its share button both.
  transition: border-color 0.2s ease-in-out;

  &:hover,
  &:focus-visible {
    border-color: rgba(var(--accent-rgb), 0.45);

    .prompt__open {
      color: var(--accent);

      svg {
        transform: translateX(3px);
      }
    }
  }
}

// Centred rather than spread, which is the same thing for a full row and the
// right thing for one picture: a lone asset sits in the middle of the card
// instead of stranded against its left edge.
.assets {
  display: flex;
  align-items: center;
  justify-content: center;
  flex-wrap: nowrap;
  gap: 0.45rem;
  width: 100%;
  margin-bottom: 0.35rem;
}

// A height and no width, so each picture keeps its own proportions and the row
// is a row of shapes rather than a row of identical boxes. max-width is the
// backstop for a very wide one, which would otherwise take the whole strip.
.assets__shot {
  flex: 0 1 auto;
  height: clamp(42px, 8vh, 64px);
  width: auto;
  max-width: 38%;
  object-fit: contain;
  border-radius: 6px;
  border: 1px solid rgba(var(--border-rgb), 0.35);
  background: rgba(var(--muted-rgb), 0.08);
}

.assets__op {
  flex: 0 0 auto;
  color: var(--muted);
  font-size: 0.8rem;
  line-height: 1;
  opacity: 0.7;
}

// The lock sits in the middle of the card and everything else goes soft behind
// it. Blurred rather than hidden: the shape of a prompt is the one thing a
// placeholder is still good for, and it can be seen without being read.
.cardlock {
  position: absolute;
  inset: 0;
  z-index: 2;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--text);
  opacity: 0.75;
  pointer-events: none;
}

// Still buttons, because they still answer when pressed. They just answer with
// a reason instead of the thing.
.prompt.is-locked,
.sheet.is-locked {
  position: relative;
  cursor: default;

  // Everything except the lock itself. user-select as well, or the words are
  // still there to be copied out of a card that will not open.
  > *:not(.cardlock) {
    filter: blur(5px);
    opacity: 0.6;
    user-select: none;
    pointer-events: none;
  }

  &:hover,
  &:focus-visible {
    border-color: rgba(var(--border-rgb), 0.35);
  }
}

.locknote {
  position: relative;
  width: min(26rem, 92vw);
  margin: auto;
  padding: clamp(1.5rem, 4vw, 2rem);
  border: 1px solid rgba(var(--border-rgb), 0.4);
  border-radius: 16px;
  background: var(--background);
  text-align: center;
}

.locknote__mark {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  margin-bottom: 0.85rem;
  border-radius: 50%;
  background: rgba(var(--muted-rgb), 0.12);
  color: var(--muted);
}

.locknote__title {
  // Carries the gap the removed line used to hold.
  margin: 0 0 1.35rem;
  font-family: 'Orbitron', sans-serif;
  font-size: 1rem;
  color: var(--text);
}


.locknote__close {
  padding: 0.5rem 1.4rem;
  border: 1px solid rgba(var(--border-rgb), 0.45);
  border-radius: 999px;
  background: none;
  color: var(--text);
  font: inherit;
  font-size: 0.82rem;
  cursor: pointer;

  &:hover {
    border-color: rgba(var(--accent-rgb), 0.6);
    color: var(--accent);
  }
}

.prompt__tag {
  padding: 0.2rem 0.55rem;
  border-radius: 999px;
  background: rgba(var(--accent-rgb), 0.1);
  color: var(--accent);
  font-size: 0.62rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.prompt__title {
  font-size: 0.95rem;
  font-weight: 700;
  color: var(--text);
}

.prompt__purpose {
  font-size: 0.78rem;
  line-height: 1.55;
  color: var(--muted);
}

// A glimpse of the prompt itself, clamped to a few lines. Enough to recognise
// a card by; the whole thing is a click away.
.prompt__peek {
  width: 100%;
  margin-top: 0.35rem;
  padding: 0.6rem 0.7rem;
  border-radius: 8px;
  background: rgba(var(--muted-rgb), 0.08);
  font-family: 'Fira Code', 'SFMono-Regular', Consolas, monospace;
  font-size: 0.68rem;
  line-height: 1.6;
  color: var(--secondary);
  white-space: pre-line;
  overflow: hidden;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
}

.prompt__open {
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
  margin-top: auto;
  padding-top: 0.65rem;
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--muted);
  transition: color 0.2s ease-in-out;

  svg {
    transition: transform 0.2s ease-in-out;
  }
}

// Popup -----------------------------------------------------------------

.prompt-detail {
  position: relative;
  display: flex;
  flex-direction: column;
  width: 100%;
  max-width: 640px;
  max-height: 82vh;
  padding: clamp(1.5rem, 3vh, 2rem);
  border-radius: 18px;
  border: 1px solid rgba(var(--border-rgb), 0.4);
  background: var(--background);
  box-shadow: 0 26px 60px rgba(0, 0, 0, 0.28);

  // Full bleed with the overlay, so the corners and the border have nothing
  // left to sit against.
  @media (max-width: 640px) {
    max-width: none;
    max-height: none;
    height: 100%;
    border: none;
    border-radius: 0;
  }
}

.prompt-detail__head {
  flex-shrink: 0;
  margin-bottom: 0.9rem;
  padding-right: 2.5rem;
}

// The row a detail view puts its label and its share button on, in both
// dialogs. Left aligned deliberately: the right of that line belongs to the
// close button, and a share sat under it is a share nobody can press.
.detail__top {
  display: flex;
  align-items: center;
  gap: 0.85rem;
  margin-bottom: 0.5rem;
}

// A card and the share button that belongs to it. The lift lives here rather
// than on the card, so the two move together instead of the card sliding out
// from under its own button.
.cell {
  position: relative;
  transition: transform 0.2s ease-in-out;

  // Only where there is a pointer to take it away again. On a touch screen
  // :hover sticks to whatever was tapped last, so the card a reader opened
  // stayed raised behind the dialog and after it closed.
  @media (hover: hover) {
    &:hover,
    &:focus-within {
      transform: translateY(-4px);
    }
  }
}

// Bottom right, on both kinds of card and at every width.
//
// The count sits beside the share rather than in the card's text, which is
// where it is on the gallery wall and on the rails at home. A number about the
// work and the control that makes another one belong to each other, and reading
// one in the body and the other in a corner made them look unrelated.
//
// Not a target itself: the card underneath is a button, and a strip laid over
// its corner would otherwise eat the taps that land there. The share takes its
// own back.
.cell__meta {
  position: absolute;
  z-index: 1;
  right: 0.9rem;
  bottom: 0.75rem;
  display: inline-flex;
  align-items: center;
  gap: 0.7rem;
  color: var(--muted);
  pointer-events: none;
}

.prompt-detail__title {
  margin: 0.5rem 0 0.25rem;
  font-size: clamp(1.05rem, 1vw + 0.8vh, 1.35rem);
  font-weight: 700;
  color: var(--text);
}

.prompt-detail__purpose {
  margin: 0;
  font-size: 0.83rem;
  line-height: 1.6;
  color: var(--muted);
}

// A label with a rule running off it, so the eye has a line to stop at. The
// purpose above and the prompt below are both grey monospace-ish blocks
// otherwise, and nothing said which was which.
.prompt-detail__heading {
  display: flex;
  align-items: center;
  gap: 0.65rem;
  margin: 1.15rem 0 0.55rem;
  font-family: 'Orbitron', sans-serif;
  font-size: 0.66rem;
  font-weight: 700;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  color: var(--muted);

  &::after {
    content: '';
    flex: 1;
    height: 1px;
    background: rgba(var(--border-rgb), 0.45);
  }
}

.prompt-detail__body {
  flex: 1;
  min-height: 0;
  margin: 0 0 1rem;
  padding: 1rem 1.1rem;
  border-radius: 10px;
  background: rgba(var(--muted-rgb), 0.08);
  font-family: 'Fira Code', 'SFMono-Regular', Consolas, monospace;
  font-size: 0.8rem;
  line-height: 1.7;
  color: var(--secondary);
  // Prompts carry deliberate line breaks; keep them, but never let a long line
  // push the dialog sideways.
  white-space: pre-wrap;
  word-break: break-word;
  overflow-y: auto;
}

.prompt__get {
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  align-self: flex-start;
  padding: 0.45rem 1rem;
  border-radius: 999px;
  border: 1px solid rgba(var(--accent-rgb), 0.4);
  background: transparent;
  font: inherit;
  font-size: 0.76rem;
  font-weight: 600;
  color: var(--accent);
  cursor: pointer;
  transition: background 0.2s ease-in-out;

  &:hover:not(:disabled) {
    background: rgba(var(--accent-rgb), 0.08);
  }

  &:disabled {
    opacity: 0.6;
    cursor: default;
  }
}

.grab {
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
}

.grab__note {
  margin: 0;
  font-size: 0.7rem;
  color: var(--muted);
}

.grab__form {
  display: flex;
  flex-direction: column;
  gap: 0.55rem;
}

.grab__field {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  max-width: 320px;

  span {
    font-size: 0.7rem;
    font-weight: 600;
    color: var(--muted);
  }

  input {
    padding: 0.5rem 0.7rem;
    border-radius: 8px;
    border: 1px solid rgba(var(--border-rgb), 0.5);
    background: var(--background);
    color: var(--text);
    font: inherit;
    // 16px stops iOS zooming the page when the field is focused.
    font-size: 16px;
  }
}

.grab__row {
  display: flex;
  align-items: center;
  gap: 0.6rem;
}

.grab__cancel {
  padding: 0.45rem 0.6rem;
  border: none;
  background: transparent;
  font: inherit;
  font-size: 0.74rem;
  color: var(--muted);
  cursor: pointer;

  &:hover { color: var(--text); }
}

.grab__error {
  margin: 0;
  font-size: 0.72rem;
  color: #dc2626;
}

.grab__done {
  margin: 0;
  font-size: 0.76rem;
  font-weight: 600;
  color: var(--accent);
}

// Spam trap: off-screen rather than display:none, which some bots skip.
.grab__trap {
  position: absolute;
  left: -9999px;
  width: 1px;
  height: 1px;
  overflow: hidden;
}

// Closed from the admin.
.closed {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: 0.6rem;
  max-width: 420px;
  margin: clamp(2rem, 8vh, 4rem) auto;
}

.closed__mark {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 52px;
  height: 52px;
  border-radius: 50%;
  background: rgba(var(--muted-rgb), 0.14);
  color: var(--muted);
}

.closed__title {
  margin: 0;
  font-size: 1.05rem;
  font-weight: 700;
  color: var(--text);
}

.closed__body {
  margin: 0;
  font-size: 0.88rem;
  line-height: 1.65;
  color: var(--secondary);
}

.closed__home {
  margin-top: 0.4rem;
  font-size: 0.8rem;
  font-weight: 600;
  color: var(--accent);
}

.empty {
  margin: 0;
  padding: clamp(2rem, 6vh, 3.5rem);
  border: 1px dashed rgba(var(--border-rgb), 0.5);
  border-radius: 14px;
  text-align: center;
  color: var(--muted);
  font-size: 0.85rem;
}

// Character detail ----------------------------------------------------------

.sheet-overlay {
  position: fixed;
  inset: 0;
  z-index: 3200;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1.5rem;
  background: rgba(0, 0, 0, 0.55);

  // Nothing inset on a phone. 24px a side is 48px of a 375px screen, and the
  // story needs the width more than the backdrop needs to be seen.
  @media (max-width: 640px) {
    padding: 0;
  }
}

.sheet-detail {
  position: relative;
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1.05fr);
  // The row must be told to fit the dialog. Left implicit it is sized to its
  // content, so a long story made a 3000px row inside a 738px box: the body
  // overflowed instead of scrolling, and with nothing scrolling the picture
  // could never follow the reading.
  grid-template-rows: minmax(0, 1fr);
  width: 100%;
  max-width: 900px;
  max-height: 82vh;
  border-radius: 18px;
  overflow: hidden;
  border: 1px solid rgba(var(--border-rgb), 0.4);
  background: var(--background);
  box-shadow: 0 26px 60px rgba(0, 0, 0, 0.28);

  @media (max-width: 640px) {
    grid-template-columns: minmax(0, 1fr);
    // Stacked, the row rule above names one row and the story lands in an
    // implicit `auto` one, which is the same failure it was written to prevent:
    // the body sizes to the story instead of scrolling it, so the sticky title
    // has no scrollport to pin against and the reading line never moves across
    // the text. The picture takes its own height here and the story takes the
    // rest.
    grid-template-rows: auto minmax(0, 1fr);
    max-width: none;
    max-height: none;
    height: 100%;
    border: none;
    border-radius: 0;
  }
}

.sheet-detail__close {
  position: absolute;
  top: 0.8rem;
  right: 0.8rem;
  // Above the sticky title, which is opaque and runs the full width of the body
  // column, so anything below it here is painted over rather than merely
  // overlapped. Both sit in the dialog's stacking context, so this is a
  // straight comparison with the 2 that title carries.
  z-index: 3;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border: none;
  border-radius: 50%;
  background: rgba(var(--background-rgb), 0.85);
  color: var(--text);
  cursor: pointer;
}

.sheet-detail__media {
  // The positioning context for the stacked frames, the dots and the caption,
  // all of which sit inside it rather than over the whole dialog.
  position: relative;
  overflow: hidden;
  background: rgba(var(--muted-rgb), 0.08);

  // Deliberately no blanket `img` rule here. There was one setting
  // object-fit: cover, and being element-qualified it outranked the
  // .sheet-detail__pic class — so the picture stayed cropped no matter what
  // that class asked for. The wash and the picture each set their own fit.

  @media (max-width: 640px) {
    aspect-ratio: 4 / 3;
    // The ratio alone is a share of the width, and on a short screen 3/4 of the
    // width is taller than the whole dialog: the story's row would be handed
    // what was left, which is nothing. The cap is what the picture may take
    // before the reading gets the rest.
    max-height: 40svh;
  }
}

.sheet-detail__body {
  // No padding at the top. The sticky title carries it instead, so the title
  // can pin flush against the scrollport rather than leaving a band of padding
  // above it for the story to travel through.
  padding: 0 clamp(1.25rem, 3vh, 2rem);
  // Space below the final beat so it can still travel up to the reading line.
  // Without it the story runs out of scroll while the last chapter is halfway
  // down the panel, and its picture could only ever be reached by its dot.
  padding-bottom: 45vh;
  overflow-y: auto;
  // Grid items default to min-height: auto, which means this would grow to fit
  // the story instead of scrolling it — and with nothing scrolling, the
  // observer band never moves across the text and the picture never changes.
  min-height: 0;
}

// Sticks to the top of the scroller rather than the viewport, which is what
// makes this work inside a modal: the body is the thing with overflow, so it is
// the thing sticky resolves against.
.sheet-detail__title {
  position: sticky;
  top: 0;
  z-index: 2;
  // Opaque, or the story reads through it as it passes underneath.
  background: var(--background);

  // Horizontal only. A negative top margin was the bug: sticky pins the margin
  // box, so pulling the box up by the panel's padding parked the title that far
  // above the scrollport and left a band at the top where the writing scrolled
  // past in the open. The body gives up its top padding instead and this
  // carries it, which puts the title flush against the top with nothing above
  // it to expose.
  margin: 0 calc(clamp(1.25rem, 3vh, 2rem) * -1) 0.75rem;
  padding: clamp(1.25rem, 3vh, 2rem) clamp(1.25rem, 3vh, 2rem) 0.6rem;
  border-bottom: 1px solid rgba(var(--border-rgb), 0.35);
}

.sheet-detail__eyebrow {
  font-family: 'Orbitron', sans-serif;
  font-size: 0.6rem;
  letter-spacing: 0.24em;
  text-transform: uppercase;
  color: var(--muted);
}

.sheet-detail__name {
  margin: 0.35rem 0 0.75rem;
  font-size: clamp(1.1rem, 1vw + 0.8vh, 1.5rem);
  font-weight: 700;
  color: var(--text);
}

.sheet-detail__traits {
  list-style: none;
  display: flex;
  flex-wrap: wrap;
  gap: 0.35rem;
  margin: 0 0 0.9rem;
  padding: 0;

  li {
    padding: 0.2rem 0.55rem;
    border-radius: 999px;
    background: rgba(var(--accent-rgb), 0.1);
    color: var(--accent);
    font-size: 0.68rem;
    font-weight: 600;

    // A trait is a short tag, but the field it comes from splits on commas, so
    // prose pasted into it arrives as one enormous "tag". The editor now warns
    // about that; this is the other half, so an existing row cannot put a
    // paragraph inside a pill.
    max-width: 100%;
    overflow: hidden;
    overflow-wrap: anywhere;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
  }
}

// The opening passage, set exactly like the parts that follow.
//
// It was a shade larger and in a stronger colour, on the reasoning that an
// opening deserves more weight. It read as a different kind of text instead,
// which is the wrong signal: this is the same voice as the rest, just first.
// The rule underneath is enough to say where it ends.
.sheet-detail__lede {
  margin: 0 0 clamp(1.5rem, 4vh, 2.5rem);
  padding-bottom: clamp(1rem, 2.5vh, 1.5rem);
  border-bottom: 1px solid rgba(var(--border-rgb), 0.3);
  font-size: 0.87rem;
  line-height: 1.75;
  color: var(--secondary);
  // Newlines are how it was typed, and the field offers no paragraph mark.
  white-space: pre-line;
}

.sheet-detail__story {
  margin: 0;
  font-size: 0.87rem;
  line-height: 1.7;
  color: var(--secondary);
  white-space: pre-line;

  &.is-empty {
    color: var(--muted);
    font-style: italic;
  }
}

.sheet-fade-enter-active,
.sheet-fade-leave-active {
  transition: opacity 0.22s ease;

  .sheet-detail {
    transition: transform 0.22s ease;
  }
}

.sheet-fade-enter-from,
.sheet-fade-leave-to {
  opacity: 0;

  .sheet-detail {
    transform: translateY(10px) scale(0.98);
  }
}

// Picture column ------------------------------------------------------------

.sheet-detail__frame {
  position: absolute;
  inset: 0;
  opacity: 0;
  transition: opacity 0.55s ease;

  &.is-on {
    opacity: 1;
  }
}

.sheet-detail__wash {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  // Pushed past the edges so the blur has no border to fade against.
  transform: scale(1.2);
  filter: blur(26px) saturate(1.1);
  opacity: 0.45;
}

.sheet-detail__pic {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  // Contain, not cover: a landscape render in a tall column was being cropped
  // to a letterbox slice, and even portraits lost their top and bottom.
  object-fit: contain;
}

.sheet-detail__controls {
  position: absolute;
  left: 50%;
  bottom: 0.9rem;
  transform: translateX(-50%);
  display: flex;
  align-items: center;
  gap: 0.15rem;
  padding: 0.2rem 0.3rem;
  border-radius: 999px;
  background: rgba(8, 11, 16, 0.6);
}

.sheet-detail__arrow {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 26px;
  height: 26px;
  padding: 0;
  border: none;
  border-radius: 50%;
  background: none;
  color: rgba(255, 255, 255, 0.8);
  cursor: pointer;
  transition: color 0.2s ease, background 0.2s ease;

  &:hover:not(:disabled) {
    color: #fff;
    background: rgba(255, 255, 255, 0.14);
  }

  // Dimmed rather than hidden, so the control keeps its shape at either end.
  &:disabled {
    color: rgba(255, 255, 255, 0.25);
    cursor: default;
  }
}

.sheet-detail__dots {
  display: flex;
  align-items: center;
  gap: 0.4rem;
  padding: 0 0.35rem;
}

.sheet-detail__dot {
  width: 7px;
  height: 7px;
  padding: 0;
  border: none;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.4);
  cursor: pointer;
  transition: background 0.25s ease, transform 0.25s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.75);
  }

  &.is-active {
    background: #fff;
    transform: scale(1.35);
  }
}

.sheet-detail__caption {
  position: absolute;
  left: 0.9rem;
  top: 0.9rem;
  padding: 0.25rem 0.6rem;
  border-radius: 999px;
  background: rgba(8, 11, 16, 0.6);
  color: rgba(255, 255, 255, 0.9);
  font-size: 0.66rem;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

// Story ---------------------------------------------------------------------

.chapter {
  // Each beat gets roughly a third of the panel whether or not the prose fills
  // it. Without a floor, a short chapter and its neighbour both sit above the
  // reading line at once and the picture skips one entirely.
  min-height: 34vh;

  & + & {
    margin-top: clamp(1.5rem, 4vh, 2.5rem);
  }
}

// A part title, not a label. It was set at 0.64rem in muted grey, which is
// smaller and quieter than the prose beneath it: the eye read the writing first
// and found the heading afterwards, if at all. A heading that has to be looked
// for is doing the opposite of its job.
.chapter__label {
  display: flex;
  align-items: center;
  gap: 0.55rem;
  margin: 0 0 0.7rem;
  font-family: 'Orbitron', sans-serif;
  font-size: 0.82rem;
  font-weight: 700;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: var(--text);

  // A short rule alongside it. Cheaper than a border on the section, and it
  // marks where one part ends and the next begins without drawing a box.
  &::before {
    content: '';
    flex-shrink: 0;
    width: 18px;
    height: 2px;
    border-radius: 2px;
    background: var(--accent);
  }
}

.chapter__text {
  margin: 0;
  font-size: 0.87rem;
  line-height: 1.75;
  color: var(--secondary);
  white-space: pre-line;
}
</style>
